import React, { useEffect, useState } from 'react';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as Icon from 'react-feather';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormToggle from '../../../components/form/FormToggle';
import FormNumberInput from '../../../components/form/FormNumberInput';
import { yesNoOptions } from '../../../utils/common';

import {
  sipProfileOptionGet,
  trunkAdd,
  trunkUpdate
} from '../../../store/slices/tenantSlices/configurationSlice';

const AddEditTrunkModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();

  const [isExpand, setIsExpand] = useState(false);
  const [sipProfileOption, setSipProfileOption] = useState([]);

  const initialValues = {
    name: '',
    register_type: 'gateway',
    username: '',
    password: '',
    domain: '',
    outbound_proxy: '',
    status: 'true',
    sip_profile: '',

    from_domain: '',
    from_user: '',
    realm: '',
    extension_in_contact: 'no',
    extension: '',
    expire_seconds: 3600,
    reg_transport: '',
    contact_params: '',
    ping: 60,
    retry_seconds: 60,
    register_proxy: ''
  };

  const validationSchema = Yup.object({
    name: Yup.string()
      .required('Name is required')
      .matches(/^[A-Za-z0-9_]+$/, 'Only letters, numbers, and _ allowed'),
    register_type: Yup.string().required('register is required'),
    domain: Yup.string().required('domain is required')
  });

  const onSubmit = async () => {
    if (isEdit) {
      try {
        const payload = { ...values, id: editRecord.id };
        if (values.register_type === 'gateway') {
          payload['username'] = '';
          payload['password'] = '';
        }
        const response = await dispatch(trunkUpdate(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Updated Successfully');
          onCloseClick(true);
          setIsExpand(false);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        const payload = { ...values };
        if (values.register_type === 'gateway') {
          payload['username'] = '';
          payload['password'] = '';
        }
        const response = await dispatch(trunkAdd(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Added Successfully');
          onCloseClick(true);
          setIsExpand(false);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  useEffect(() => {
    if (isEdit) {
      setValues({ ...editRecord, status: String(editRecord?.status) });
    }
  }, [editRecord]);

  const getSipProfileOption = async () => {
    try {
      const response = await dispatch(sipProfileOptionGet()).unwrap();
      if (response) {
        const options = response.map((val) => ({
          label: val.sip_profile_name,
          value: val.name
        }));
        setFieldValue('sip_profile', options?.find((x) => x.label === 'External')?.value);
        setSipProfileOption(options);
      } else {
        setSipProfileOption([]);
      }
    } catch (error) {
      setSipProfileOption([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getSipProfileOption();
  }, []);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Trunk' : 'Add Trunk'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="crms" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormInput
                name="name"
                label="Name"
                placeholder="Enter name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                trueName="Gateway"
                falseName="SIP"
                name="register_type"
                label="Register Type"
                value={String(values.register_type === 'gateway')}
                onChange={() =>
                  setFieldValue(
                    'register_type',
                    values.register_type === 'gateway' ? 'sip' : 'gateway'
                  )
                }
              />
            </Col>
            {values.register_type === 'sip' && (
              <Col lg="3">
                <FormInput
                  name="username"
                  label="Username"
                  placeholder="Enter Username"
                  value={values.username}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            )}
            {values.register_type === 'sip' && (
              <Col lg="3">
                <FormInput
                  name="password"
                  label="Password"
                  placeholder="Enter Password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            )}
            <Col lg="3">
              <FormInput
                name="domain"
                label="Domain"
                placeholder="Enter Domain"
                value={values.domain}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormInput
                name="outbound_proxy"
                label="Outbound Proxy"
                placeholder="Enter Outbound Proxy"
                value={values.outbound_proxy}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="status"
                label="Status"
                value={values.status}
                onChange={(e) => setFieldValue('status', e)}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="sip_profile"
                label="SIP Profile"
                placeholder="Select SIP Profile"
                value={values.sip_profile}
                options={sipProfileOption}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
          </Row>
          <div className="w-100 py-2 px-3 bg-primary text-white fw-bold d-flex justify-content-between align-items-center">
            <div>Optional Information</div>
            <div className="cursor-pointer" onClick={() => setIsExpand(!isExpand)}>
              {isExpand ? <Icon.MinusCircle /> : <Icon.PlusCircle />}
            </div>
          </div>
          {isExpand && (
            <Row className="py-2">
              <Col lg="3">
                <FormInput
                  name="from_domain"
                  label="From Domain"
                  placeholder="Enter From Domain"
                  value={values.from_domain}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3">
                <FormInput
                  name="from_user"
                  label="From User"
                  placeholder="Enter From User"
                  value={values.from_user}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3">
                <FormInput
                  name="realm"
                  label="Realm"
                  placeholder="Enter Realm"
                  value={values.realm}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3">
                <FormSelect
                  name="extension_in_contact"
                  label="Extension In Contact"
                  placeholder="Select Extension In Contact"
                  value={values.extension_in_contact}
                  options={yesNoOptions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3">
                <FormInput
                  name="extension"
                  label="Extension"
                  placeholder="Enter Extension"
                  value={values.extension}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3">
                <FormNumberInput
                  name="expire_seconds"
                  label="Expire Seconds"
                  placeholder="Enter Expire Seconds"
                  value={values.expire_seconds}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3">
                <FormInput
                  name="reg_transport"
                  label="Reg-Transport"
                  placeholder="Enter Reg-Transport"
                  value={values.reg_transport}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3">
                <FormInput
                  name="contact_params"
                  label="Contact Params"
                  placeholder="Enter Contact params"
                  value={values.contact_params}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3">
                <FormNumberInput
                  name="ping"
                  label="Ping"
                  placeholder="Enter Ping"
                  value={values.ping}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3">
                <FormNumberInput
                  name="retry_seconds"
                  label="Retry Seconds"
                  placeholder="Enter Retry Seconds"
                  value={values.retry_seconds}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3">
                <FormInput
                  name="register_proxy"
                  label="Register Proxy"
                  placeholder="Enter Register Proxy"
                  value={values.register_proxy}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
          )}
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            setIsExpand(false);
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="crms" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditTrunkModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditTrunkModal;
